import { useState } from 'react';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { getUserAction } from 'store/modules/auth/actions';
import {
  getCookiePropName,
  getCookieSessionToken,
  getEnvironment
} from 'utils/session';
import { CandidateService } from 'services/user.service';
import _ from 'lodash';
import {
  TextField,
  Fab,
  Box,
  Typography,
  CircularProgress,
  Button
} from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import Page from 'components/templates/Page';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { withMask } from 'utils/mask';
import { validateDocument } from 'utils/validation';
import api from 'services/api';
import Cookies from 'js-cookie';
import { closeSnackbar, useSnackbar } from 'notistack';

import { useStyles } from './styles';

function Requirements() {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  const domain = process.env.REACT_APP_DOMAIN ?? '';
  const tokenPropName = getCookiePropName(domain, 'token');
  const userTypePropName = getCookiePropName(domain, 'userType');

  const userName =
    user?.name && user?.surname ? `${user?.name} ${user?.surname}` : '';

  const [docError, setDocError] = useState({ lastValue: '', message: '' });
  const [phoneError, setPhoneError] = useState({ lastValue: '', message: '' });

  const showPhoneError = (currentPhone: string) => {
    if (!phoneError.lastValue) {
      return false;
    }
    return currentPhone === phoneError.lastValue;
  };

  const showDocError = (currentDoc: string) => {
    if (!docError.lastValue) {
      return false;
    }
    return currentDoc === docError.lastValue;
  };

  const schema = Yup.object().shape({
    name: Yup.string()
      .max(165)
      .trim()
      .matches(/^[^\s]+(\s[^\s]+)+(\s[^\s]+)*$/, 'Nome completo deve ter sobrenome')
      .required('Nome completo é um campo obrigatório.'),
    cellPhone: Yup.string()
      .required('Celular é um campo obrigatório.')
      .min(14, 'Celular deve ter 11 caracteres.'),
    document: Yup.string()
      .required('CPF é um campo obrigatório.')
      .test('document', 'CPF inválido', (document) =>
        validateDocument({ value: document, type: 'cpf' })
      )
  });

  const redirectUser = () => {
    setLoading(true);
    const env = getEnvironment(domain);
    const link =
      env !== ''
        ? `http://app.${env}.gria.com.br/auth?action=application`
        : `http://app.gria.com.br/auth?action=application`;
    localStorage.clear();
    Cookies.remove(tokenPropName, { domain, secure: true });
    Cookies.remove(userTypePropName, { domain });
    delete api.defaults.headers.common.Authorization;
    window.location.replace(link);
    setLoading(false);
  };

  const action = (key: number | string) => (
    <Button
      onClick={() => {
        closeSnackbar(key);
      }}
      style={{ fontFamily: 'Nunito', color: '#ffffff' }}
    >
      Entendi
    </Button>
  );

  const updateUser = ({ name: completeName, cellPhone, document }: any) => {
    setLoading(true);
    if (user) {
      const updatedUser = _.cloneDeep(user);

      const [firstName, surname] = completeName.split(' ');

      updatedUser.name = firstName;
      updatedUser.surname = surname;
      updatedUser.document = withMask(document, 'cpf');
      updatedUser.cellPhone = cellPhone;

      CandidateService.updateUser(updatedUser)
        .then(() => {
          const token = getCookieSessionToken();
          if (token) {
            dispatch(getUserAction(token));
          }
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          if (
            typeof message === 'string' &&
            message.includes('surname contains only special characters')
          ) {
            enqueueSnackbar('Não é permitido o uso de caractere especial.', {
              variant: 'error',
              persist: true,
              action: (key) => action(key)
            });
          } else if (
            typeof message === 'string' &&
            message.includes('duplicate key value violates unique constraint')
          ) {
            if (message.includes('cellPhone')) {
              setPhoneError({
                lastValue: cellPhone,
                message: 'Esse celular já está cadastrado com outra conta'
              });
            } else if (message.includes('document')) {
              setDocError({
                lastValue: document,
                message: 'Esse CPF já está cadastrado com outra conta'
              });
            }
          } else {
            enqueueSnackbar('Erro ao tentar realizar cadastro.', {
              variant: 'error'
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const title = (
    <span>
      Queremos te
      <br />
      conhecer melhor!
    </span>
  );

  return (
    <Page title={title} subtitle="Por favor, complete os campos abaixo">
      <Formik
        initialValues={{
          name: userName,
          cellPhone: withMask(user?.cellPhone ?? '', 'cel'),
          document: user?.document || ''
        }}
        validationSchema={schema}
        onSubmit={(values) => updateUser(values)}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values
        }) => (
          <Form className={styles.container} onSubmit={handleSubmit}>
            <TextField
              label="Seu nome completo"
              value={values.name}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
              name="name"
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <Box>
              <Typography className={styles.emailLabel}>SEU EMAIL</Typography>
              <Typography className={styles.email}>{user?.email}</Typography>
            </Box>
            <TextField
              label="Seu celular"
              value={withMask(values.cellPhone, 'cel')}
              error={
                (touched.cellPhone && !!errors.cellPhone) ||
                showPhoneError(values.cellPhone)
              }
              helperText={
                (touched.cellPhone && errors.cellPhone) ||
                (showPhoneError(values.cellPhone) && phoneError.message)
              }
              name="cellPhone"
              onBlur={handleBlur}
              onChange={handleChange}
              inputProps={{ maxLength: 14 }}
            />
            <TextField
              label="CPF"
              value={withMask(values.document, 'cpf')}
              error={
                (touched.document && !!errors.document) ||
                showDocError(values.document)
              }
              helperText={
                (touched.document && errors.document) ||
                (showDocError(values.document) && docError.message)
              }
              name="document"
              onBlur={handleBlur}
              onChange={handleChange}
              inputProps={{ maxLength: 14 }}
            />
            <Box className={styles.buttonContainer}>
              <Fab
                disabled={loading}
                onClick={() => {
                  redirectUser();
                }}
              >
                {loading ? (
                  <CircularProgress style={{ color: '#fff' }} size={24} />
                ) : (
                  <ArrowBack />
                )}
              </Fab>
              <Fab type="submit" disabled={loading}>
                {loading ? (
                  <CircularProgress style={{ color: '#fff' }} size={24} />
                ) : (
                  <ArrowForward />
                )}
              </Fab>
            </Box>
          </Form>
        )}
      </Formik>
    </Page>
  );
}

export default Requirements;
